import { Controller } from "@hotwired/stimulus"
import { Loggable } from "./concerns/loggable"
import { ShowHide } from "./concerns/showHide"

export default class extends Controller {

  static targets = [
    "intervalType",
    "durationMinutes",
    "durationSeconds",
    "durationMessage"
  ]
  static values = {
    state: String,
    name: String,
    position: Number,
    playClick: Boolean,
  }

  static outlets = ["timer-player"]

  initialize() {
    Loggable(this, {debug: true})
    ShowHide(this, { hiddenClassName: "d-none" })
  }

  connect() {
    // si no funciona stimulus no se ve el player.
    this.show(this.element)
    this.setState("loading")
    this.initClicks()
    this.initProgressBarElements()
    this.setState("stopped")
    this.renderDuration()
  }

  initClicks(){
    this.currentClick = 0
  }

  initDurations(){
    this._totalDuration = parseInt(this.durationMinutesTarget.value) * 60 + parseInt(this.durationSecondsTarget.value)
    document.dispatchEvent(new CustomEvent("interval:durationChanged"))
  }

  renderDuration(){
    this.initDurations()
    // render duration in minutes and seconds with trailing zeros on seconds
    this.durationMessageTarget.innerText = `${Math.floor(this._totalDuration / 60)}:${(this._totalDuration % 60).toString().padStart(2, '0')}`
  }
  
  incClick(){
    this.currentClick++
  }

  start(){
    this.initClicks()
    // cacheo valores para minimizar cálculos en el click()
    this.initDurations()
    this.setSiblingsSegmentsStatus()
    this.resetProgress()
    this.setState("playing")
  }

  continue(){
    // igual a start() pero sin inicializaciones.
    this.setState("playing")
    this.updateProgress()
  }

  stop(){
    this.setState("stopped")
  }

  finish(){
    this.setState("finished")
  }

  setState(newValue){
    this.stateValue = newValue
    switch(this.stateValue) {
      case "loading":
        this.setProgressSegmentInactive()
        break
      case "stopped":
        this.setProgressSegmentInactive()
        break
      case "finished":
        this.setProgressSegmentCompleted()
        break
      case "pre-playing":
        this.setProgressSegmentActive()
        break
      case "playing":
        this.setProgressSegmentActive()
        break
    }
    document.dispatchEvent(new CustomEvent("interval:stateChanged", {detail: {state: this.stateValue}}))
  }

  // es el callback del worker
  // se ejecuta en cada tick
  click(){
    this.playNeededAudio()
    this.render()
    this.checkDuration()
    this.incClick()
    this.updateProgress()
  }

  // Si seteamos una duración, paramos cuando llegamos a esa duración
  checkDuration(){
    if (this.isOverDuration()) {
      this.finish()
    }
  }

  isOverDuration(){
    return this.currentClick >= this._totalDuration
  }

  playNeededAudio(){
    throw "playNeededAudio() not implemented"
  }

  render(){
    this.renderAction()
    this.renderNextActionPreview()
    this.renderElapsedTime()
    this.timerPlayerOutlet.renderPresentation(this.presentation())
  }

  presentation() {
    return {type: "basic", name: this.nameValue, elapsed: Math.min(this.currentClick + 1, this._totalDuration), duration: this._totalDuration}
  }

  /* called from timer_player_controller */
  renderIntervalName(){
    this.timerPlayerOutlet.intervalNameTarget.innerText = this.nameValue
  }

  // this.currentClick is elapsed seconds
  // show minutes and seconds with trailing zeros on seconds
  renderElapsedTime(){
    let humanClick = this.currentClick + 1
    let message = `${Math.floor(humanClick / 60)}:${(humanClick % 60).toString().padStart(2, '0')}`
    message += ` / ${Math.floor(this._totalDuration / 60)}:${(this._totalDuration % 60).toString().padStart(2, '0')}`
    this.timerPlayerOutlet.elapsedTimeMessageTarget.innerText = message
  }

  renderAction(){
    this.timerPlayerOutlet.actionMessageTarget.innerText = this.actionMessage()
  }

  renderNextActionPreview(){
    if (this.hasNextAction()) {
      this.timerPlayerOutlet.nextActionMessageTarget.innerText = `${this.timerPlayerOutlet.strNextValue}: ${this.nextActionMessage()}`
    } else {
      this.timerPlayerOutlet.renderNextIntervalPreview()
    }
  }

  renderFirstActionPreview(){
    this.timerPlayerOutlet.nextActionMessageTarget.innerText = `${this.timerPlayerOutlet.strNextValue}: ${this.firstActionMessage()}`
  }

  hasNextAction(){
    return false
  }

  actionMessage(){
    return this.nameValue
  }

  nextActionMessage(){
    return this.nameValue
  }

  firstActionMessage(){
    return this.nameValue
  }

  // === Sibling Interval methods ===
  getAllPreviousIntervals() {
    if (this.hasTimerPlayerOutlet) {
      return this.timerPlayerOutlet.loadIntervals().filter((_, index) => {
        return (index < this.positionValue - 1)
      })
    }
    return []
  }

  getAllNextIntervals() {
    if (this.hasTimerPlayerOutlet) {
      return this.timerPlayerOutlet.loadIntervals().filter((_, index) => {
        return (index > this.positionValue - 1)
      })
    }
    return []
  }


  // === ProgressBar methods ===

  // segmentElement is the current segment in the progress bar
  // progressElement is the fill element inside the segment
  initProgressBarElements() {
    if (this.hasTimerPlayerOutlet && this.timerPlayerOutlet.hasProgressBarTarget) {
      const segments = this.timerPlayerOutlet.progressBarTarget.querySelectorAll('.timer-progress-segment')
      const index = this.positionValue ? this.positionValue - 1 : this.timerPlayerOutlet.loadIntervals().indexOf(this)
      this.segmentElement = segments[index]
      this.progressElement = this.segmentElement ? this.segmentElement.querySelector('.timer-progress-fill') : null
    }
  }

  setProgressSegmentActive() {
    if (this.segmentElement) {
      this.segmentElement.classList.add("active")
      this.segmentElement.classList.remove("completed")
    }
  }

  setProgressSegmentInactive() {
    if (this.segmentElement) {
      this.segmentElement.classList.remove("active")
    }
  }

  setProgressSegmentCompleted() {
    if (this.segmentElement) {
      this.segmentElement.classList.add("completed")
      this.segmentElement.classList.remove("active")
    }
    if (this.progressElement) {
      this.progressElement.style.width = "100%"
    }
  }

  updateProgress(){
    if (this.progressElement) {
      const percent = Math.min(100, (this.currentClick / this._totalDuration) * 100)
      this.progressElement.style.width = `${percent}%`
    }
  }

  resetProgress(){
    if (this.progressElement) {
      this.progressElement.style.width = "0%"
    }
  }

  setSiblingsSegmentsStatus(){
    this.completeAllPreviousSegments()
    this.resetAllNextSegments()
  }

  completeAllPreviousSegments() {
    this.getAllPreviousIntervals().forEach((interval, index) => {
      interval.setProgressSegmentCompleted()
    })
  }

  resetAllNextSegments() {
    this.getAllNextIntervals().forEach((interval, index) => {
      interval.setProgressSegmentInactive()
      interval.resetProgress()
    })
  }

}
